import { apiError, orderClient } from '@/lib/api/ts-rest';
import { createLogger } from '@/lib/logger';
import { queryKeys } from '@/lib/query-keys';
import { invalidateOrderAndTravelEsimViews } from '@/lib/travel-esim-cache';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

const log = createLogger('useTravelEsimOrderActions');

/** Octave admin: upload the QR code image of a travel eSIM order line. */
export function useAttachTravelEsimQrCode(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, file }: { itemId: string; file: File }) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await orderClient.attachTravelEsimQrCode({
        params: { id: orderId, itemId },
        body: formData,
      });
      if (res.status !== 201) {
        throw apiError(res, 'Le téléversement du QR code a échoué');
      }
      return { ...res.body, itemId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.orders.travelEsimsRoot(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.orders.travelEsimQrCode(orderId, result.itemId),
      });
      toast.success('QR code téléversé, vérifiez-le puis envoyez-le au bénéficiaire');
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Le téléversement du QR code a échoué',
      );
      log.error('Attach travel eSIM QR code error', error);
    },
  });
}

/** Octave admin: e-mail the attached QR code to the beneficiary (again if needed). */
export function useSendTravelEsimQrCode(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      const res = await orderClient.sendTravelEsimQrCode({
        params: { id: orderId, itemId },
      });
      if (res.status !== 201) throw apiError(res, "L'envoi du QR code a échoué");
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.orders.travelEsimsRoot(),
      });
      toast.success('QR code envoyé au bénéficiaire');
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "L'envoi du QR code a échoué",
      );
      log.error('Send travel eSIM QR code error', error);
    },
  });
}

/** Octave admin: record the provider's eSIM number on a travel eSIM order line. */
export function useSetTravelEsimNumber(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, esimNumber }: { itemId: string; esimNumber: string }) => {
      const res = await orderClient.setTravelEsimNumber({
        params: { id: orderId, itemId },
        body: { esimNumber },
      });
      if (res.status !== 200) {
        throw apiError(res, "L'enregistrement du numéro eSIM a échoué");
      }
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.orders.travelEsimsRoot(),
      });
      toast.success('Numéro eSIM enregistré');
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "L'enregistrement du numéro eSIM a échoué",
      );
      log.error('Set travel eSIM number error', error);
    },
  });
}

/** Organization member: send the QR code of an eSIM again to its beneficiary (once Octave sent it). */
export function useResendTravelEsimQrCode(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const res = await orderClient.resendTravelEsimQrCode({
        params: { id },
      });
      if (res.status !== 201) throw apiError(res, "L'envoi du QR code a échoué");
      return res.body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.orders.travelEsimsRoot(),
      });
      toast.success('QR code renvoyé au bénéficiaire');
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "L'envoi du QR code a échoué",
      );
      log.error('Resend travel eSIM QR code error', error);
    },
  });
}

/**
 * Octave admin: confirm a recharge plan was loaded on the beneficiary's
 * eSIM, which e-mails them; again on a loaded line, resends the e-mail.
 */
export function useMarkTravelEsimPlanLoaded(orderId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      itemId,
      resend,
    }: {
      itemId: string;
      resend?: boolean;
    }) => {
      const res = await orderClient.markTravelEsimPlanLoaded({
        params: { id: orderId, itemId },
      });
      if (res.status !== 200) {
        throw apiError(
          res,
          resend
            ? "Le renvoi de l'e-mail a échoué"
            : 'La confirmation du chargement a échoué',
        );
      }
      return res.body;
    },
    onSuccess: (_data, { resend }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.orders.travelEsimsRoot(),
      });
      toast.success(
        resend
          ? 'E-mail renvoyé au bénéficiaire'
          : 'Forfait chargé : e-mail envoyé au bénéficiaire',
      );
    },
    onError: (error) => {
      toast.error(error.message);
      log.error('Mark travel eSIM plan loaded error', error);
    },
  });
}

/**
 * Octave admin: hard-delete an international eSIM with its plans (the
 * orders left without any line go too).
 */
export function useDeleteTravelEsim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (travelEsimId: string) => {
      const res = await orderClient.deleteTravelEsim({
        params: { id: travelEsimId },
      });
      if (res.status !== 200) {
        throw apiError(res, "La suppression de l'eSIM a échoué");
      }
      return res.body;
    },
    onSuccess: () => {
      invalidateOrderAndTravelEsimViews(queryClient);
      toast.success('eSIM internationale supprimée');
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "La suppression de l'eSIM a échoué",
      );
      log.error('Delete travel eSIM error', error);
    },
  });
}
